import { AuthenticationService } from "../authenticationService";
import { APIService } from "../apiService";

class BrandAPIService extends APIService {
  constructor(authenticationService: AuthenticationService) {
    super(authenticationService, "/v1/brands");
  }

  // ── Public endpoints (no auth) ────────────────────────────────────────────

  async postImageCheckUpload(body: {
    fileName: string;
    mimeType: string;
  }): Promise<{ signedUrl: string; s3Key: string; fileId: string; viewUrl: string | null }> {
    return this.publicRequest("POST", "/image-check-upload", body);
  }

  async postImageCheck(body: {
    imageS3Keys: string[];
    brandName: string;
    productName: string;
    productTypes: string[];
    productType?: string;
    dispatchLocation?: "UK" | "US" | "Other";
  }): Promise<{ fileId: string }> {
    return this.publicRequest("POST", "/image-check", body);
  }

  async getImageCheck(fileId: string): Promise<{
    meetsMinimumRequirements: boolean | null;
    reasoning: string | null;
    status?: string;
    autoFixed: boolean;
    complianceChecked: boolean;
    editedImageS3Keys: string[];
    editedImageSignedUrls: string[];
  }> {
    return this.publicRequest("GET", `/image-check/${encodeURIComponent(fileId)}`);
  }

  async postBrandSubmission(body: object): Promise<{ id: string; token: string }> {
    return this.publicRequest("POST", "/submission", body);
  }

  async postExtractFromUrls(body: { urls: string[] }): Promise<{ jobId: string }> {
    return this.publicRequest("POST", "/extract-from-urls", body);
  }

  async getExtractJob(jobId: string): Promise<{
    jobId: string;
    status: "PENDING" | "RUNNING" | "COMPLETED" | "FAILED";
    urls: string[];
    results: Array<{
      url: string;
      status: "extracted" | "no_info" | "failed";
      extracted: Record<string, unknown> | null;
      imageS3Keys: string[];
      imageSignedUrls: string[];
      errorMessage: string | null;
    }>;
    errorMessage: string | null;
  }> {
    return this.publicRequest("GET", `/extract-job/${encodeURIComponent(jobId)}`);
  }

  async getBrandSubmissionStatus(token: string): Promise<any> {
    return this.publicRequest("GET", `/submission/status/${encodeURIComponent(token)}`);
  }

  async getBrandSubmissionForResubmit(token: string): Promise<{
    id: string;
    vendorId?: string;
    brandInfo: {
      contactFirstName: string;
      contactLastName: string;
      contactEmail: string;
      bpOwner: string;
      commercialPoc: string;
      registeredCompanyName: string;
      brandDisplayName: string;
      productTypes: string[];
      dispatchLocation?: "UK" | "US" | "Other";
      dispatchCountry?: string;
      // AI-302: brand-level compliance fields
      manufacturingType?: string;
      manufacturingSetupDetails?: string | null;
      foodSafetyCertifications?: string[];
      foodSafetyCertificationOther?: string | null;
      facilityCertificationS3Key?: string | null;
      ukApprovalNumber?: string | null;
      feiNumber?: string | null;
      establishmentApprovalNumber?: string | null;
      ukResponsiblePerson?: { companyName: string; contactDetails: string } | null;
      euResponsiblePerson?: { companyName: string; contactDetails: string } | null;
      additionalDisclosures?: string | null;
      recallsAcknowledged?: boolean;
    };
    products: Array<{
      productId: string;
      productName: string;
      variantSize: string;
      barcode: string;
      rrp: string;
      unitCost?: string;
      currency: "GBP" | "USD" | "EUR";
      chargeVat: boolean;
      ingredients: string;
      suggestedUse: string;
      description: string;
      searchTerms?: string;
      d2cLink?: string;
      isConsumable: boolean;
      isGwp?: boolean;
      hasExpiryDate?: boolean;
      shelfLifeMonths?: number;
      countryOfOrigin?: string;
      commodityHsCode?: string;
      primaryPackaging?: string;
      dietaryInfo?: string[];
      specSheetS3Key?: string | null;
      imageS3Keys: string[];
      imageCheckPassed: boolean;
      // AI-302: per-product compliance fields
      coaFrequency?: string;
      coaS3Key?: string | null;
      requiresRefrigeration?: boolean | null;
      storageTemperature?: string | null;
      storageTemperatureOther?: string | null;
      handlingInstructions?: string | null;
      containsAnimalOrigin?: boolean | null;
      animalOriginSources?: string[];
      animalOriginSourceOther?: string | null;
      bseTseDeclaration?: string | null;
      bseTseS3Key?: string | null;
      hiddenPoaoDeclaration?: string | null;
      fishWildCaught?: boolean | null;
      catchCertificatesAvailable?: boolean | null;
      catchCertificateS3Key?: string | null;
      isCertifiedOrganic?: boolean;
      organicCertificationS3Key?: string | null;
    }>;
    rejectionReason?: string;
  }> {
    return this.publicRequest("GET", `/submission/resubmit/${encodeURIComponent(token)}`);
  }

  // ── Authenticated endpoints (JWT) ─────────────────────────────────────────

  async getBrandSubmissions(params?: {
    status?: string;
    bpOwner?: string;
    brandName?: string;
  }): Promise<{ items: any[] }> {
    return this.jwtRequest("GET", "/submissions", undefined, params);
  }

  async getBrandSubmissionById(id: string): Promise<any> {
    return this.jwtRequest("GET", `/submissions/${encodeURIComponent(id)}`);
  }

  async patchBrandSubmissionDecision(
    id: string,
    body: { decision: "APPROVE" | "REJECT"; reason?: string }
  ): Promise<any> {
    return this.jwtRequest("PATCH", `/submissions/${encodeURIComponent(id)}/decision`, body);
  }

  async patchBrandSubmissionPdpAssignee(id: string, body: { pdpAssignee: "Fiona" | "Thania" | null }): Promise<any> {
    return this.jwtRequest("PATCH", `/submissions/${encodeURIComponent(id)}/pdp-assignee`, body);
  }

  async patchBrandSubmissionComplianceReview(
    id: string,
    body: { productId: string; complianceCategorisation: "Food Supplement" | "Food" | "Hybrid" }
  ): Promise<any> {
    return this.jwtRequest("PATCH", `/submissions/${encodeURIComponent(id)}/compliance-review`, body);
  }

  async patchSubmissionVendor(id: string, body: { vendorId: string }): Promise<any> {
    return this.jwtRequest("PATCH", `/submissions/${encodeURIComponent(id)}/vendor`, body);
  }

  async getVendorSearch(q: string): Promise<{ items: Array<{ id: string; name: string }> }> {
    return this.publicRequest("GET", "/vendor-search", undefined, { q });
  }
}

// Singleton for public form pages — only publicRequest methods are called on this instance
// so authenticationService is never accessed at runtime.
const brandAPIService = new BrandAPIService(undefined as unknown as AuthenticationService);
export { BrandAPIService, brandAPIService };
